import { useEffect, useRef } from 'react';
import { hasBackend } from '@/lib/supabase';

/** Captcha del login (VLT-018) — vendor aislado en la UI («verificación de
 *  seguridad»). El widget SOLO aparece si el Worker sirve una site key
 *  (/api/auth/captcha); sin configurar, este componente no pinta nada y el
 *  login funciona como siempre. El token viaja a Supabase Auth, que lo
 *  verifica ÉL con el secret (captcha del panel de auth) — activar todo es
 *  poner dos llaves, cero código nuevo. */

interface TurnstileApi {
  render(el: HTMLElement, opts: {
    sitekey: string;
    callback: (token: string) => void;
    'expired-callback': () => void;
    'error-callback': () => void;
    theme: 'auto';
  }): string;
  remove(id: string): void;
}
declare global {
  interface Window { turnstile?: TurnstileApi }
}

let siteKeyPromise: Promise<string | null> | null = null;
function fetchSiteKey(): Promise<string | null> {
  siteKeyPromise ??= (async () => {
    try {
      const r = await fetch('/api/auth/captcha');
      if (!r.ok) return null;
      return ((await r.json()) as { siteKey?: string | null }).siteKey ?? null;
    } catch {
      return null;
    }
  })();
  return siteKeyPromise;
}

let scriptPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  scriptPromise ??= new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.onload = () => resolve();
    s.onerror = () => { scriptPromise = null; reject(new Error('turnstile')); };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export function CaptchaGate({ onToken }: { onToken: (token: string | null) => void }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasBackend) return;
    let alive = true;
    let widgetId: string | null = null;
    void (async () => {
      const siteKey = await fetchSiteKey();
      if (!alive || !siteKey) return;
      try {
        await loadScript();
      } catch {
        return; // sin script no hay widget — el backend decidirá si lo exige
      }
      if (!alive || !ref.current || !window.turnstile) return;
      widgetId = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        callback: (token) => onToken(token),
        'expired-callback': () => onToken(null),
        'error-callback': () => onToken(null),
        theme: 'auto',
      });
    })();
    return () => {
      alive = false;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
    // onToken estable por useState setter; el widget se monta UNA vez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hasBackend) return null;
  return <div ref={ref} className="mt-3 flex justify-center empty:hidden" />;
}
